<script lang="ts">
	import { format } from 'svelte-i18n';
	import { createEventDispatcher } from 'svelte';
	import { push } from 'svelte-spa-router';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import {
		Toolbar,
		Breadcrumbs,
		BreadcrumbsFlexWrapper,
		IconButton,
		Button,
		DropdownMenu,
		MenuItemList,
	} from '@Shelly/shelly-design-system';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import type { GridLayout } from '../helpers/camera-monitoring-settings';

	type GridLayoutOption = { value: GridLayout; icon: string; cols: number };
	type DropdownActionItem = { id: string; title: string; icon: string; action: () => void };
	type DropdownActionGroup = {
		groupTitle: string;
		isGroupTitle: boolean;
		items: DropdownActionItem[];
	};

	export let editMode = false;
	export let availableGridLayouts: GridLayoutOption[] = [];
	export let selectedLayout: GridLayout;
	export let dropdownActionsData: DropdownActionGroup[] = [];

	const dispatch = createEventDispatcher<{
		'layout-change': { layout: GridLayout };
		'toggle-edit': null;
	}>();

	let windowInnerWidth = 0;
	let showDropdownMenuGrouped = false;

	$: isMobile = windowInnerWidth < SystemScreenBreakpoints.MOBILE_MAX;

	const goToAllDashboards = () => push('/dash');

	// New layout breadcrumb mirrors DashToolbar: "All Dashboards / Camera Monitoring"
	$: cameraBreadcrumbsLinks = isMobile
		? [{ label: $format('tab_camera_monitoring', { default: 'Camera Monitoring Center' }) }]
		: [
				{
					label: $format('label_all_dashboards', { default: 'All Dashboards' }),
					onClick: goToAllDashboards,
				},
				{ label: $format('tab_camera_monitoring', { default: 'Camera Monitoring Center' }) },
			];

	const handleDropdownItemClick = (action: () => void) => {
		action?.();
		showDropdownMenuGrouped = false;
	};
</script>

<svelte:window bind:innerWidth={windowInnerWidth} />

<Toolbar class="mb-m" isSticky>
	<svelte:fragment slot="breadcrumbs">
		{#if $showNewLayout}
			<BreadcrumbsFlexWrapper hideSlotRight>
				<div slot="left" class="flex-1 min-width-0 max-width-fit-content">
					<div class="d-flex width-full align-items-center">
						{#if isMobile}
							<IconButton
								aria-label={$format('button_back', { default: 'Back' })}
								variant="secondary"
								icon="fas fa-chevron-left"
								class="mr-s-plus"
								on:click={goToAllDashboards}
							/>
						{/if}
						<Breadcrumbs
							class="flex-1 min-width-0 max-width-fit-content"
							listClasses="width-full"
							aria-label="Camera Monitoring breadcrumbs"
							type="buttons"
							links={cameraBreadcrumbsLinks}
						/>
					</div>
				</div>
			</BreadcrumbsFlexWrapper>
		{:else}
			<Breadcrumbs
				aria-label="Camera Monitoring breadcrumbs"
				links={[
					{
						label: $format('tab_camera_monitoring', {
							default: 'Camera Monitoring Center',
						}),
					},
				]}
			/>
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="rightSide">
		{#if !editMode}
			{#if availableGridLayouts.length > 1}
				<div class="flex-overflow-hidden gap-xs width-0 width-md-auto">
					{#each availableGridLayouts as layout (layout.value)}
						<IconButton
							size="m"
							icon={layout.icon}
							variant={selectedLayout === layout.value ? 'primary' : 'secondary'}
							on:click={() => dispatch('layout-change', { layout: layout.value })}
							aria-label={`${layout.value} grid layout`}
						/>
					{/each}
				</div>
			{/if}
			<DropdownMenu bind:show={showDropdownMenuGrouped}>
				<IconButton
					slot="trigger"
					aria-label="Open camera actions"
					icon="fas fa-ellipsis-vertical"
					variant="secondary"
					on:click={() => (showDropdownMenuGrouped = !showDropdownMenuGrouped)}
				/>

				{#each dropdownActionsData as dropdownAction}
					<MenuItemList
						title={dropdownAction.groupTitle}
						isGroupTitle={dropdownAction.isGroupTitle}
					/>
					{#each dropdownAction.items as dropdownMenuItem}
						<MenuItemList
							isGroupItem
							title={dropdownMenuItem.title}
							icon={dropdownMenuItem.icon}
							on:click={() => handleDropdownItemClick(dropdownMenuItem.action)}
						/>
					{/each}
				{/each}
			</DropdownMenu>
		{:else}
			<Button size="m" on:click={() => dispatch('toggle-edit')}>
				{$format('button_exit_edit', { default: 'Exit edit mode' })}
			</Button>
		{/if}
	</svelte:fragment>
</Toolbar>
